import { useEffect, useMemo, useState } from "react";

// MUI
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { Box, Fade, IconButton, Modal, Stack, SxProps, Typography } from "@mui/material";

// Components
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import ArrayDialog from "@/DataPages/components/common/ListColumnDialog/ArrayDialog";

// Hooks
import { useSmartTableColGenerator } from "@/Common/hooks/SmartTableColGenerator/useSmartTableColGenerator";
import useAggregationExtended from "@Layouts/components/Tables/DataSmartTable/aggregation/Aggregation";

// Types
import { MappingColumnValue, MappingDialogContent, MappingFilter, PopupModalContent } from "@/Common/types/meta/mappingFilters";

// Helpers
import { generateSx, getRowClassName, getRowId } from "@/Layouts/components/Tables/DataSmartTable/helpers";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// Constants
import { tableKeys } from "@/Constants/tableKeys";
import { BASE_TABLE_PROPS, DEFAULT_HEADER_HEIGHT } from "@/Washouts/constants";

const slots = {
	toolbar: CommonToolbar,
	noRowsOverlay: NoRowsOverlay,
	noResultsOverlay: NoRowsOverlay,
};

const columnVisibilityModel = {
	_id: false,
};

const noContentObject = {
	title: `No data to show`,
	className: "!text-montserrat",
};

interface IRepairOrdersTableProps {
	items: object[];
	isPaginating: boolean;
	mappingFilters: MappingFilter[];
}

export default function RepairOrdersTable(props: Readonly<IRepairOrdersTableProps>) {
	const { items = [], isPaginating, mappingFilters } = props ?? {};

	const [selectedFilter, setSelectedFilter] = useState<MappingFilter | null>(null);

	const aggregations = useAggregationExtended({
		repairOrderFinalizedOrdersAggregation: {},
	});

	useEffect(() => setSelectedFilter(mappingFilters?.[0] ?? null), [mappingFilters]);

	const mappingCols = useMemo(() => selectedFilter?.columns ?? [], [selectedFilter]);

	const apiRef = useGridApiRef();
	const [sx, setSx] = useState<SxProps>({});

	useEffect(() => {
		if (!apiRef.current?.subscribeEvent) {
			return () => {};
		}
	}, [items]);

	useEffect(() => {
		setSx(generateSx(apiRef));
		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => {
			setSx(generateSx(apiRef));
		});
	}, [mappingCols, apiRef]);

	const slotProps = useMemo(
		() => ({
			toolbar: {
				filter: {
					hide: false,
				},
				search: {
					useQuickFilter: true,
				},
				customComponent: (
					<TableHeader
						isPaginating={isPaginating}
						selectedFilter={selectedFilter}
						filtersData={mappingFilters}
						onFilterChange={setSelectedFilter}
					/>
				),
			},
			basePopper: {
				sx: { zIndex: 1300 },
			},
			noRowsOverlay: noContentObject,
			noResultsOverlay: noContentObject,
		}),
		[isPaginating, selectedFilter]
	);

	const [modalContent, setModalContent] = useState<PopupModalContent | null>(null);

	const augmentedMappingCols = useMemo(() => {
		return mappingCols.map((col) => {
			const variant = col.variant ?? "";
			const isModal = col.columnType === "CUSTOM" && ["MODAL", "ARRAY"].includes(variant);

			if (!isModal) {
				return col;
			}

			return {
				...col,
				modalClickHandler: (content: MappingDialogContent) => setModalContent({ variant, content }),
			};
		});
	}, [mappingCols]);

	const { columns, pinnedColumns } = useSmartTableColGenerator(augmentedMappingCols);

	return (
		<Stack className="!h-full relative">
			<DataSmartTable
				apiRef={apiRef}
				rows={items}
				columns={columns}
				pinnedColumns={pinnedColumns}
				getRowClassName={getRowClassName}
				getRowId={getRowId}
				rowHeight={25}
				maxRows={19}
				initialState={{
					aggregation: {
						model: {
							"repairOrder.repairOrderRecord.repairOrderMisc.customerLaborTotal_customerLaborTotal": "sum",
							"repairOrder.repairOrderRecord.repairOrderMisc.customerPartsTotal_customerPartsTotal": "sum",
							"repairOrder.repairOrderRecord.repairOrderMisc.internalLaborTotal_internalLaborTotal": "sum",
							"repairOrder.repairOrderRecord.repairOrderMisc.internalPartsTotal_internalPartsTotal": "sum",
							"repairOrder.repairOrderRecord.repairOrderMisc.warrantyLaborTotal_warrantyLaborTotal": "sum",
							"repairOrder.repairOrderRecord.repairOrderMisc.warrantyPartsTotal_warrantyPartsTotal": "sum",
							repairOrderNumber_repairOrderNumber: "repairOrderFinalizedOrdersAggregation",
							transactionTypeDate_transactionTypeDate: "size",
						},
					},
				}}
				filterStoreConfig={{ key: tableKeys.DATA_PAGES.REPAIR_ORDERS }}
				columnVisibilityModel={columnVisibilityModel}
				headerHeight={DEFAULT_HEADER_HEIGHT}
				customProps={{
					...BASE_TABLE_PROPS,
					tableContainerClassName:
						"[&_.toolbar-actions]:!absolute [&_.toolbar-actions]:!right-[0px] [&_.toolbar-actions]:!top-[-8px]",
					tableClassName: {
						"[&_.MuiDataGrid-columnHeader--withRightBorder]:!border-none": true,
						"[&_.MuiDataGrid-filler--pinnedLeft]:!border-r-transparent": true,
					},
					aggregationFooterClassName: "aggregation-footer regular-footer",
					expandableRows: false,
					hide: false,
					customAggregationsFn: { ...aggregations },
				}}
				slotProps={slotProps}
				slots={slots}
				sx={sx}
				disableColumnMenu
				enableCustomScrollbars
				disableRowSelectionOnClick
			/>

			<PopupWrapper modalContent={modalContent} closePopup={() => setModalContent(null)} />
		</Stack>
	);
}

// MARK:- Supporting Components
interface LoadingIndicatorProps {
	isLoading: boolean;
	label?: string;
	fadeDuration?: number;
	className?: string;
}

function LoadingIndicator(props: Readonly<LoadingIndicatorProps>) {
	const { isLoading, className, fadeDuration = 400, label = "Data is still loading..." } = props;

	return (
		<Fade timeout={fadeDuration} in={isLoading} unmountOnExit className={className}>
			<div>
				<LoadingSpinner label={label} className="items-center" />
			</div>
		</Fade>
	);
}

// MARK:- Table Header
interface TableHeaderProps {
	isPaginating: boolean;
	selectedFilter: MappingFilter | null;
	filtersData: MappingFilter[];
	onFilterChange: (filter: MappingFilter | null) => void;
}

function TableHeader(props: Readonly<TableHeaderProps>) {
	const { isPaginating, selectedFilter, filtersData, onFilterChange } = props;

	return (
		<Box className="pb-4">
			<SmartSelector
				selected={selectedFilter}
				selectBy="name"
				showBy="name"
				selectDefault
				options={filtersData}
				classes="!w-[200px]"
				onChangeAction={(value) => onFilterChange(value as MappingFilter)}
			/>

			{isPaginating && <LoadingIndicator isLoading={isPaginating} className="pt-2" />}
		</Box>
	);
}

// MARK:- Modal Popup
interface IPopupWrapperProps {
	modalContent: PopupModalContent | null;
	closePopup: () => void;
}

function PopupWrapper(props: Readonly<IPopupWrapperProps>) {
	const { modalContent, closePopup } = props ?? {};

	if (!modalContent) {
		return null;
	}
	const { variant, content } = modalContent;

	if (variant === "MODAL") {
		return <ModalPopup modalContent={content.data as MappingColumnValue[]} closeModal={closePopup} />;
	}
	return <ArrayDialog dialogContent={content} handleOnCloseDialog={closePopup} />;
}

function CloseIcon(props: { onClick: () => void }) {
	const { onClick } = props;

	return (
		<IconButton className="!absolute !right-0 !top-0 !text-gray-400" aria-label="close" onClick={onClick}>
			<CloseRoundedIcon />
		</IconButton>
	);
}

function ModalContent(props: { modalContent: MappingColumnValue[] }) {
	const { modalContent } = props;

	return modalContent.map((item, index) => (
		<Box key={`${index}_${item.title}`}>
			<Typography className="!font-montserrat !text-sm !font-medium !text-default">{item.title}</Typography>
			<Typography className="!font-sans !text-sm !text-default">{`${item.value || "-"}`}</Typography>
		</Box>
	));
}

interface ModalPopupProps {
	modalContent: MappingColumnValue[] | null;
	closeModal: () => void;
}

function ModalPopup(props: ModalPopupProps) {
	const { modalContent, closeModal } = props;
	const hasContent = modalContent && modalContent.length > 0;

	const renderContent = () => {
		if (!hasContent) {
			return <Typography className="!font-montserrat !text-default italic text-center py-5">No data available</Typography>;
		}

		return (
			<Stack spacing={2}>
				<ModalContent modalContent={modalContent} />
			</Stack>
		);
	};

	return (
		<Modal open={modalContent != null} onClose={closeModal}>
			<Box className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[235px] bg-white rounded-[10px] shadow-2xl p-6">
				<CloseIcon onClick={closeModal} />
				{renderContent()}
			</Box>
		</Modal>
	);
}
